/**
 * Awards & Achievements store – single source of truth.
 * GET runs once per session (ensureFetched); after each POST we refetch and update store.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  ArtistService,
  type ArtistAwardApiItem,
} from "@/services/artistService";
import { useArtistDetailsStore } from "@/stores/artistDetailsStore";

export type { ArtistAwardApiItem };

interface AwardsState {
  data: ArtistAwardApiItem[] | null;
  isLoading: boolean;
  saveLoading: boolean;
  error: string | null;
  hasFetchedInSession: boolean;

  setData: (data: ArtistAwardApiItem[] | null) => void;
  fetchAwards: () => Promise<ArtistAwardApiItem[] | null>;
  ensureFetched: () => Promise<ArtistAwardApiItem[] | null>;
  /** POST items (new awards or achievements) then GET to refresh store. */
  addItems: (items: ArtistAwardApiItem[]) => Promise<void>;
  /** DELETE award/achievement by id then GET to refresh store. */
  deleteItem: (id: number) => Promise<void>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

let inFlightFetch: Promise<ArtistAwardApiItem[] | null> | null = null;

const initialState: Omit<
  AwardsState,
  | "setData"
  | "fetchAwards"
  | "ensureFetched"
  | "addItems"
  | "deleteItem"
  | "setLoading"
  | "setError"
  | "reset"
> = {
  data: null,
  isLoading: false,
  saveLoading: false,
  error: null,
  hasFetchedInSession: false,
};

export const useAwardsStore = create<AwardsState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setData: (data) => set({ data, error: null }),

      fetchAwards: async () => {
        if (inFlightFetch) return inFlightFetch;
        inFlightFetch = (async () => {
          set({ isLoading: true, error: null });
          try {
            const data = await ArtistService.getAwards();
            set({
              data: data ?? null,
              error: null,
              isLoading: false,
              hasFetchedInSession: true,
            });
            void useArtistDetailsStore.getState().fetchArtistDetails();
            return data;
          } catch (err) {
            set({
              error: "Failed to load awards & achievements",
              isLoading: false,
            });
            return null;
          } finally {
            inFlightFetch = null;
          }
        })();
        return inFlightFetch;
      },

      ensureFetched: async () => {
        const { hasFetchedInSession, fetchAwards, data } = get();
        if (hasFetchedInSession) return data;
        return fetchAwards();
      },

      addItems: async (items: ArtistAwardApiItem[]) => {
        if (items.length === 0) return;
        set({ saveLoading: true, error: null });
        try {
          await ArtistService.updateAwards(items);
          await get().fetchAwards();
        } catch (err) {
          set({
            error: "Failed to save. Please try again.",
            saveLoading: false,
          });
          throw err;
        } finally {
          set({ saveLoading: false });
        }
      },

      deleteItem: async (id: number) => {
        set({ error: null });
        try {
          await ArtistService.deleteAward(id);
          await get().fetchAwards();
        } catch (err) {
          set({
            error: "Failed to delete. Please try again.",
          });
          throw err;
        }
      },

      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),

      reset: () => {
        inFlightFetch = null;
        set(initialState);
      },
    }),
    {
      name: "awards-storage",
      partialize: () => ({}),
    }
  )
);
